import { Injectable } from '@angular/core';
import * as cfg from 'src/config.json';
import { Order, OrderItem } from 'src/app/models';
import PouchDB from 'pouchdb';
import { ErrorStateMatcher } from '@angular/material/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { LogService } from './log.service';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class DbToolsService {
  constructor(
    private log: LogService,
    private http: HttpClient
  ) { }

  /**
   * 
   * @param first app version in format 'v.N.N.N'
   * @param second app version in format 'v.N.N.N'
   * @returns 1 if second newer than first, -1 if first newer than second, 0 if same
   */
  compareAppVer(first: string, second: string) {
    const firstAppVer = first.split('.').slice(1).map((n) => Number(n));
    const secondAppVer = second.split('.').slice(1).map((n) => Number(n));

    for (let i in firstAppVer) {
      if (firstAppVer[i] < secondAppVer[i]) {
        // console.warn(`${first} < ${second}`);
        return 1;
      }
      if (firstAppVer[i] > secondAppVer[i]) {
        // console.warn(`${first} > ${second}`);
        return -1;
      }
    }
    // console.warn(`${first} == ${second}`);
    return 0;
  }


  /**
   * Check if database exists in the memory of the browser.
   *
   * @param dbName name of pouchdb db to check
   * @returns true if database exists and is not empty, else false
   */
  async checkIfDbExists(dbName: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      let db = new PouchDB(dbName);
      const info = await db.info();
      if (info.doc_count === 0 && info.update_seq === 0) {
        await db.destroy();
        resolve(false);
      } else {
        // db.close();
        db = null;
        resolve(true);
      }
      // this.log.log(info)
      // resolve(true);
    });
  }

  randSafeGen(n: number = 5): string {
    let rand = '';
    // https://www.ietf.org/rfc/rfc3986.txt
    const urlSafes =
      '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'; // -._~'
    const getChar = () => urlSafes[Math.floor(Math.random() * urlSafes.length)];
    for (let i = 0; i < n; i++) {
      rand += getChar();
    }
    return rand;
  }

  /**
   * Function for order comparison
   *
   * @param oOld
   * @param oNew
   */
  diffOrders(oOld: Order, oNew: Order): { diff: any[]; chNum: number } {
    const delta = this.diffItems(oOld.items, oNew.items);
    if (oOld.comment !== oNew.comment) {
      delta.chNum += 1;
      delta.diff.push({ new_comment: oNew.comment });
    }
    return delta;
  }

  /**
   * Function for order items comparison
   *
   * @param iOld old element to be compared
   * @param iNew new element to be compared
   */
  diffItems(
    iOld: OrderItem[],
    iNew: OrderItem[]
  ): { diff: any[]; chNum: number } {
    const diff: OrderItem[] = [];
    let priorityChange = 0;
    iOld.forEach((v, i, a) => {
      const c = {
        ...iNew.find(
          (vn) => vn.ref === v.ref && vn.variant?.idx === v.variant?.idx
        ),
      };
      if ('ref' in c) {
        // change quantities
        c.quant -= v.quant;
        diff.push(c);
        // change priority
        c.priority -= v.priority;
        if (c.priority !== 0) {
          priorityChange += 1;
        }
      } else {
        // remove deleted
        const deleted: OrderItem = { ...v };
        deleted.quant = -deleted.quant;
        diff.push(deleted);
      }
    });
    // add new items
    iNew.forEach((v) => {
      if (
        iOld.findIndex(
          (vo) => vo.ref === v.ref && vo.variant?.idx === v.variant?.idx
        ) < 0
      ) {
        diff.push(v);
      }
    });
    return {
      diff,
      chNum:
        priorityChange +
        diff.reduce((p, c, i, a) => (p += Math.abs(c.quant)), 0),
    };
  }

  async getDocById(id: string, db: any): Promise<any> {
    try {
      const doc = await db.get(id);
      return doc;
    } catch (err) {
      if (err.error && 'name' in err) {
        return err.name;
      } else {
        this.log.log(`db-tools -> getDocById id: ${id}; ERROR ${err}`);
        throw new Error(err);
      }
    }
  }

  async getDocByIdPrefix(prefix: string, db: any, include_docs = true): Promise<any> {
    // https://pouchdb.com/api.html#batch_fetch
    try {
      const docs = await db.allDocs({
        include_docs,
        startkey: prefix,
        endkey: `${prefix}\ufff0`,
      });
      return docs;
    } catch (err) {
      this.log.log(err);
    };
  };

  /**
   * 
   * @param prefix 
   * @param dbUrl eg. https://b.lookat.menu/rest3-public
   * @param include_docs 
   * @returns 
   */
  async getDocByIdPrefixWithHttp(prefix: string, dbUrl: string, include_docs = true): Promise<any> {
    const data = {
      include_docs,
      startkey: prefix,
      endkey: `${prefix}\ufff0`,
    }
    try {
      let resp = await this.http.post(`${dbUrl}/_all_docs`,data).toPromise() as any;
      return resp;
    } catch (error) {
      this.log.log(`GDBIDPWH#1 ${JSON.stringify(error)}`);
    };
  };

  async createDoc(
    doc: any,
    db: any,
    id: string = null,
    suffix: string | boolean = false
  ): Promise<any> {
    try {
      if (!id) {
        id = `${this.randSafeGen(cfg.default['tools-db'].randIdLength)}${suffix ? suffix : ''
          }`;

        let alreadyExist = true;
        while (alreadyExist) {
          try {
            await db.get(id);
            id = id + this.randSafeGen(cfg.default['tools-db'].randIdLength);
          } catch (err) {
            if (err.name === 'not_found') {
              alreadyExist = false;
            }
          }
        }
      }
      doc._id = id;
      if ('_rev' in doc) { delete doc._rev; };
      const resp = await db.put(doc);
      return resp;
    } catch (err) {
      this.log.log(err);
    }
  }

  async deleteByIdDoc(docId: any, db: any): Promise<any> {
    try {
      const doc = await db.get(docId);
      const resp = await db.remove(doc);
      return resp;
    } catch (err) {
      this.log.log('name' in err ? err.name : err);
    }
  }

  async deleteByIdDocSafe(docId: any, db: any): Promise<any> {
    try {
      const doc = await db.get(docId);
      const docDeleted = { _id: doc._id, _rev: doc._rev, _deleted: true };
      // doc._deleted = true
      const resp = await db.put(docDeleted);
      return resp;
    } catch (err) {
      this.log.log('name' in err ? err.name : err);
    }
  }

  async updateDoc(doc: any, db: any): Promise<any> {
    // this.log.log(doc)
    try {
      const oldDoc = await db.get(doc._id);
      if (oldDoc._rev !== doc._rev) {
        throw new Error('document updated by someone else');
      }
      const resp = await db.put(doc);
      return resp;
    } catch (err) {
      this.log.log(`dbt_updateDoc#1: ${JSON.stringify(err)}`);
    }
  }

  async deletedDocsIds(db: any): Promise<any> {
    const ret: string[] = [];
    return new Promise((resolve, reject) => {
      db.changes({ filter: (d) => d._deleted })
        .on('change', (c) => ret.push(c.id))
        .on('complete', () => resolve(ret))
        .on('error', (e) => reject(e));
    });
  }

  getIdsFromLastDays(ids: Array<String>, noOfDays = 1) {
    return ids.filter((el) => {
      return Number(el.split(':')[2]) > Date.now() - noOfDays * (24 * 60 * 60 * 1000);
    })
  }
  async deletedDocs(db: any) {
    const ids = await this.deletedDocsIds(db);
    const lastIds = this.getIdsFromLastDays(ids);
    // console.log(lastIds);
    return Promise.allSettled(
      lastIds.map((id) =>
        db.get(id, { revs: true, open_revs: 'all' }).then(async (x) => {
          const revs = (x[0].ok as any)._revisions;
          const lastRev = revs.start - 1 + '-' + revs.ids[1];
          return db.get(id, { rev: lastRev });
        })
      )
    );
  }

  async createOrUpdateDoc(
    doc: any,
    db: any,
    id: string = null,
    suffix: boolean | string = false
  ): Promise<any> {
    return doc._rev
      ? await this.updateDoc(doc, db)
      : await this.createDoc(doc, db, id, suffix);
  }

  async createOrUpdateDoc2({ doc, db, id = null, suffix = null }): Promise<any> {
    if (!id) {
      id = doc._id;
    }
    if (!('_id' in doc)) {
      doc._id = id;
    }
    try {
      if (!id || id == '') { throw ({ name: 'not_found' }); };
      const oldDoc = await db.get(id);
      if (!('_rev' in doc)) {
        doc._rev = oldDoc._rev;
        return await db.put(doc);
      }
      if (oldDoc._rev !== doc._rev) {
        this.log.log('update conflict');
        return false;
      }
      return await db.put(doc);
    } catch (error) {
      if (error.name === 'not_found') {
        return await this.createDoc(doc, db, id, suffix);
      }
    }
    // console.debug('COUD2-dbt failed')
  }

  /**
   *
   * @param id of sync
   * @param renew if previous sync should be canceled (connection to new db) default `false`
   *
   * @returns true if new connection should be established, false if already exist
   */
  async checkSync(id, dict, renew = false): Promise<boolean> {
    if (id in dict && renew) {
      let canceled = false;
      this.log.log(`canceling sync: ${id}`);
      try {
        this.log.log(await this.cancelSync2(dict[id]));
        delete dict[id];
      } catch (err) {
        this.log.log(err);
      }
      return true;
    }
    if (id in dict) {
      return false;
    }
    return true;
  }

  async cancelSync(syncHand): Promise<void> {
    let canceled = false;
    syncHand.on('complete', () => {
      canceled = true;
      this.log.log('canceling complete');
    });
    // syncHand.on('error', (e) => this.log.log(e));
    syncHand.cancel();
    // while (!canceled) {await this.sleep(100)};
  }

  async cancelSync2(syncHand): Promise<boolean> {
    this.log.log('cancel sync 2');
    return new Promise((resolve, reject) => {
      syncHand.on('complete', () => resolve(true));
      syncHand.on('error', (e) =>
        reject(`error during sync cancelation: ${JSON.stringify(e)}`)
      );
      syncHand.cancel();
    });
  }

  async sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  matcher = new ImmedietError();
}

//https://material.angular.io/components/input/examples#input-error-state-matcher
class ImmedietError implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}
